(() => {
  if (customElements.get('x-modal')) return;

  class XModal extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.innerHTML = `
        <style>
          :host { display: contents; }

          :host{
            --x-modal-max-w: min(92vw, 920px);
            --x-modal-max-h: 86vh;
            --x-modal-radius: 12px;
            --x-modal-bg: #fff;
            --x-modal-padding: 0px;
            --x-modal-shadow: 0 12px 36px rgba(0,0,0,.18);
            --x-modal-backdrop: rgba(0,0,0,.2);

            --x-modal-mobile-max-h: min(92vh, 640px);
            --x-modal-mobile-radius: 16px 16px 0 0;
          }

          .backdrop{
            position: fixed; inset: 0;
            display: flex; align-items: center; justify-content: center;
            background: var(--x-modal-backdrop);
            z-index: 2147483647;
            opacity: 0; visibility: hidden; pointer-events: none;
            transition: opacity .18s ease-out, visibility 0s linear .18s;
          }
          :host([open]) .backdrop{
            opacity: 1; visibility: visible; pointer-events: auto;
            transition: opacity .18s ease-out, visibility 0s;
          }

          .panel-wrap{ position: relative; }

          .panel{
            background: var(--x-modal-bg);
            width: var(--x-modal-max-w);
            max-height: var(--x-modal-max-h);
            border-radius: var(--x-modal-radius);
            box-shadow: var(--x-modal-shadow);
            padding: var(--x-modal-padding);
            transform: translateY(8px) scale(.98);
            opacity: 0;
            transition: transform .18s cubic-bezier(.2,.7,.3,1), opacity .18s ease-out;
            will-change: transform, opacity;
            overflow: auto;
          }
          :host([open]) .panel{ transform:none; opacity:1; }

          .close {
            position: absolute; 
            top:0px; 
            right:0px; 
            transform: translateX(calc(100% + 1rem));
            border:0; background:transparent;
            width: 4.8rem;
            height: 7.4rem;
            display:flex; 
            align-items:center; 
            justify-content:center;
            color: #000;
            border-radius:15rem; 
            cursor:pointer;
            background: #FFF;
          }

          .panel-content {
            overflow: auto;
          }

          .close .icon-close {
            display: flex;
          }

          .close .close-text {
            display: none;
          }

          @media (prefers-reduced-motion: reduce){
            .backdrop, .panel { transition: none !important; }
          }


          @media (max-width: 749px){
            .backdrop{
              display: flex;
              align-items: flex-end;      
              justify-content: center;
            }
            .panel-wrap{
              width: 100%;
            }
            .panel{
              width: 100vw;               
              max-width: 100vw;
              max-height: var(--x-modal-mobile-max-h); 
              border-radius: var(--x-modal-mobile-radius);
              box-shadow: 0 -8px 24px rgba(0,0,0,.18);
              transform: translateY(100%) scale(1);
              opacity: 1;                  
              transition: transform 0.3s cubic-bezier(0,0,0.2,1),
                opacity 0.3s cubic-bezier(0,0,0.2,1);
              will-change: transform;
              overscroll-behavior: contain;
            }
            :host([open]) .panel{
              transform: translateY(0);
            }

            .close {
              top: 0rem;
              right: 50%;
              transform: none;           
              width: auto;
              height: auto;
              min-height: 4.8rem;
              transform: translate(50%, calc(-100% - 2.4rem));
              display: flex;
              padding-left: 2.5rem;
              padding-right: 2.5rem;
            }

            .close .close-text {
              display: block;
              font-size: 1.4rem;
              line-height: 160%;
              font-weight: 600;
              color: #484848;
            }

            .close .icon-close {
              display: none;
            }
              
            .panel{
              padding-bottom: max(var(--x-modal-padding), env(safe-area-inset-bottom));
            }
          }
        </style>

        <div class="backdrop" data-modal-close>
          <div class="panel-wrap">
            <button class="close" type="button" aria-label="Close" data-modal-close>
              <svg class="icon icon-close" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>

              <span class="close-text"></span>
            </button>
            <div class="panel" role="dialog" aria-modal="true" aria-labelledby="modal-title">
              <slot></slot>
            </div>
          </div>
        </div>
      `;

      this._onDocClick = this._onDocClick.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onShadowClick = this._onShadowClick.bind(this);
    }

    connectedCallback(){
      if (this.id) {
        const dup = [...document.querySelectorAll(`x-modal#${CSS.escape(this.id)}`)].find(el => el !== this);
        if (dup) dup.remove();
      }
      if (this.parentNode !== document.body) document.body.appendChild(this);
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKey);
      this.shadowRoot.addEventListener('click', this._onShadowClick);
      this.shadowRoot.querySelector('.close-text').textContent = window.accessibilityStrings?.close || 'Close';
    }
    disconnectedCallback(){
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKey);
      this.shadowRoot.removeEventListener('click', this._onShadowClick);
    }

    _onDocClick(e){
      const openBtn = e.target.closest?.('[data-modal-open]');
      if (openBtn && openBtn.getAttribute('data-modal-open') === this.id) {
        e.preventDefault();
        this.open();
      }
    }
    _onShadowClick(e){
      if (e.target.closest('[data-modal-close]')) { e.preventDefault(); this.close(); return; }
      const bd = this.shadowRoot.querySelector('.backdrop');
      if (e.target === bd) this.close();
    }
    _onKey(e){
      if (!this.hasAttribute('open')) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
    }

    open(){
      this.setAttribute('open','');
      const docEl = document.documentElement, body = document.body;
      const n = +(body.dataset.gmLockCount || 0) + 1;
      body.dataset.gmLockCount = String(n);
      if (n === 1) {
        const sbw = window.innerWidth - docEl.clientWidth;
        body.dataset.gmPrevOverflow = body.style.overflow || '';
        body.dataset.gmPrevPaddingRight = getComputedStyle(body).paddingRight;
        docEl.style.setProperty('--width-scrollbar', sbw + 'px');
        if (sbw > 0) {
          const pr = parseFloat(getComputedStyle(body).paddingRight) || 0;
          body.style.paddingRight = (pr + sbw) + 'px';
        }
        body.style.overflow = 'hidden';
      }
    }
    close(){
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      const body = document.body, docEl = document.documentElement;
      const n = Math.max((+(body.dataset.gmLockCount || 1)) - 1, 0);
      body.dataset.gmLockCount = String(n);
      if (n === 0) {
        body.style.overflow = body.dataset.gmPrevOverflow || '';
        body.style.paddingRight = body.dataset.gmPrevPaddingRight || '';
        delete body.dataset.gmPrevOverflow;
        delete body.dataset.gmPrevPaddingRight;
        docEl.style.removeProperty('--width-scrollbar');
      }
    }
  }

  customElements.define('x-modal', XModal);
})();